import { useForm } from '@tanstack/react-form';

import { FormDialog } from '@/components/composed/FormDialog';
import { FormField } from '@/components/composed/FormField';
import { fieldErrorMessages } from '@/lib/forms/field-errors';
import { Input } from '@/components/ui/input';
import { notify } from '@/components/composed/notify';
import { ApiError } from '@/lib/api/errors';
import {
  invoiceFormSchema,
  toInvoiceInput,
  type InvoiceFormValues,
} from '../schemas/invoice-form';
import { useCreateInvoice } from '../api/invoice-mutations';

/*
 * Create dialog (spec §7.2, §7.6). TanStack Form + Zod (validators.onChange).
 * Server field errors from Problem Details `errors[]` are mapped back onto the
 * form via validators.onSubmitAsync so they render inline under each field.
 */
export function InvoiceEditDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createInvoice = useCreateInvoice();

  const form = useForm({
    defaultValues: {
      clientId: '',
      amountMajor: 0,
      currency: 'USD',
      dueAt: '',
    } as InvoiceFormValues,
    validators: {
      onChange: invoiceFormSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          await createInvoice.mutateAsync(toInvoiceInput(value));
          return null;
        } catch (error) {
          if (error instanceof ApiError && error.fieldErrors?.length) {
            // Map server field names back onto form fields. amountMinor maps to
            // the amountMajor input the user edits.
            const fields: Record<string, string> = {};
            for (const fe of error.fieldErrors) {
              const name =
                fe.field === 'amountMinor' ? 'amountMajor' : fe.field;
              fields[name] = fe.message;
            }
            return { fields };
          }
          return {
            form:
              error instanceof ApiError
                ? error.message
                : 'Failed to create invoice.',
          };
        }
      },
    },
    onSubmit: () => {
      notify.success('Invoice created.');
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Create invoice"
      description="Amounts are entered in major units and stored as minor units."
      onSubmit={() => void form.handleSubmit()}
      submitLabel="Create"
      pending={createInvoice.isPending}
    >
      <form.Field name="clientId">
        {(field) => (
          <FormField
            label="Client ID"
            errors={fieldErrorMessages(field.state.meta.errors)}
          >
            {({ id }) => (
              <Input
                id={id}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder="cli_0001"
              />
            )}
          </FormField>
        )}
      </form.Field>

      <form.Field name="amountMajor">
        {(field) => (
          <FormField
            label="Amount"
            errors={fieldErrorMessages(field.state.meta.errors)}
          >
            {({ id }) => (
              <Input
                id={id}
                type="number"
                step="0.01"
                value={String(field.state.value)}
                onChange={(e) =>
                  field.handleChange(e.target.valueAsNumber || 0)
                }
                onBlur={field.handleBlur}
              />
            )}
          </FormField>
        )}
      </form.Field>

      <form.Field name="currency">
        {(field) => (
          <FormField
            label="Currency"
            errors={fieldErrorMessages(field.state.meta.errors)}
          >
            {({ id }) => (
              <Input
                id={id}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                maxLength={3}
              />
            )}
          </FormField>
        )}
      </form.Field>

      <form.Field name="dueAt">
        {(field) => (
          <FormField
            label="Due date"
            errors={fieldErrorMessages(field.state.meta.errors)}
          >
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </FormField>
        )}
      </form.Field>
    </FormDialog>
  );
}
